import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { Printer, Bluetooth, Camera, Wrench, Battery, CheckCircle2, AlertTriangle, Usb, Download, Smartphone, QrCode } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

// In-app version of /app/POS_SETUP_GUIDE.md so cashiers can read setup
// instructions without leaving the admin console.
const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-[#E1E5DF] rounded-xl p-4 sm:p-6 mb-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-5 w-5 text-[#2F5233]" />
      <h3 className="font-heading font-extrabold text-lg sm:text-xl text-[#1C2826]">{title}</h3>
    </div>
    <div className="text-sm text-[#1C2826] leading-relaxed space-y-2">{children}</div>
  </div>
);

const Step = ({ n, title, children }) => (
  <div className="border-l-2 border-[#2F5233] pl-3 py-1 mb-3">
    <div className="font-bold text-[#2F5233] text-sm">Step {n} — {title}</div>
    <div className="text-sm text-[#1C2826] mt-1 space-y-1">{children}</div>
  </div>
);

export default function AdminPrinterSetup() {
  // Direct Play Store deep link — opens the RawBT app page on any Android device.
  const RAWBT_PLAY_URL = "https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter";
  // Mirror this page's URL so the cashier can scan from the tablet camera and
  // land back here without typing.
  const thisPageUrl = typeof window !== "undefined" ? `${window.location.origin}/admin?tab=printer` : "";

  // Test-print via react-to-print. The @page rule is built dynamically from
  // the live test-block's measured height so the print preview shows exactly
  // the paper length needed (no extra whitespace below).
  const testPrintRef = useRef(null);
  const PX_TO_MM = 0.264583;
  const TEST_CSS = `
    html, body { margin: 0; padding: 0; background: #fff; }
    .pos-print-receipt {
      display: block !important;
      position: static !important;
      visibility: visible !important;
      left: auto !important;
      width: 80mm;
      padding: 2mm 3mm 3mm 3mm;
      font-family: 'Courier New', monospace;
      font-size: 10pt;
      line-height: 1.2;
      color: #000;
      box-sizing: border-box;
    }
    .pos-print-receipt .pr-center { text-align: center; }
    .pos-print-receipt .pr-heading { font-size: 14pt; font-weight: 800; letter-spacing: 1px; line-height: 1.15; }
    .pos-print-receipt .pr-small   { font-size: 9pt; line-height: 1.25; }
    .pos-print-receipt .pr-tiny    { font-size: 8pt; margin-top: 0.5mm; }
    .pos-print-receipt .pr-rule    { border-top: 1px dashed #000; margin: 1.2mm 0; }
    .pos-print-receipt .pr-row     { display: flex; justify-content: space-between; gap: 6px; }
  `;
  const printTest = useReactToPrint({
    contentRef: testPrintRef,
    documentTitle: "Arbe-PrinterTest",
    pageStyle: () => {
      const hPx = testPrintRef.current ? testPrintRef.current.scrollHeight : 0;
      const hMm = Math.max(40, Math.ceil(hPx * PX_TO_MM) + 4);
      return `@page { size: 80mm ${hMm}mm; margin: 0; }\n${TEST_CSS}`;
    },
  });
  const now = new Date();
  const testStamp = now.toLocaleString("en-PH");

  return (
    <div className="max-w-3xl mx-auto" data-testid="printer-setup-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#1C2826]">POS Setup Guide for Cashiers</h2>
          <p className="text-sm text-[#4A5D5A] mt-1">
            One-time setup for the <b>Officom OC-T80A 80mm thermal printer</b> and the
            built-in camera barcode scanner on the Xiaomi Redmi Pad 2 tablet.
          </p>
        </div>
        <a
          href="/Arbe_Minimart_Printer_Setup.pdf"
          download
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2F5233] hover:bg-[#1F3D24] text-white text-sm font-bold shadow-sm"
          data-testid="download-printer-pdf-btn"
        >
          <Download className="h-4 w-4" />
          Download PDF
        </a>
      </div>

      <Section icon={Smartphone} title="Setting up on a tablet? Start here">
        <p className="text-[#4A5D5A]">
          You'll do steps 1–7 below <b>on the cashier tablet</b> (Redmi Pad 2). To
          jump straight from this laptop screen to the tablet, open the tablet
          camera and scan the QR code — it loads this same page on the tablet so
          you can tap the buttons directly. Then tap <b>Install RawBT</b> below to
          grab the print-bridge app in one tap.
        </p>
        <div className="grid sm:grid-cols-[auto_1fr] gap-4 items-center pt-2">
          <div className="bg-white border border-[#E1E5DF] rounded-lg p-3 inline-flex flex-col items-center" data-testid="setup-qr">
            <QRCodeCanvas value={thisPageUrl} size={144} includeMargin={false} fgColor="#1C2826" />
            <div className="mt-1.5 flex items-center gap-1 text-[10px] text-[#738380] uppercase tracking-wider">
              <QrCode className="h-3 w-3" /> Scan from tablet
            </div>
          </div>
          <div className="space-y-2">
            <a
              href={RAWBT_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-[#2F5233] hover:bg-[#1F3D24] text-white font-bold py-3 rounded-lg text-sm"
              data-testid="install-rawbt-btn"
            >
              ▸  Install RawBT (free, ~10 MB)
            </a>
            <a
              href="/Arbe_Minimart_Printer_Setup.pdf"
              download
              className="block w-full text-center border border-[#1C2826] text-[#1C2826] hover:bg-[#1C2826] hover:text-white font-bold py-3 rounded-lg text-sm"
              data-testid="download-pdf-inline"
            >
              <Download className="inline-block h-4 w-4 mr-1" /> Download PDF guide
            </a>
            <div className="text-[11px] text-[#738380] leading-snug">
              <b className="text-[#1C2826]">Tip for the Redmi Pad 2:</b> open the
              tablet camera, point it at the QR code on the left, then tap the
              link that pops up. This page opens on the tablet — tap Install RawBT
              and follow Step 1 onward.
            </div>
          </div>
        </div>
      </Section>

      <Section icon={Bluetooth} title="1) Officom OC-T80A — One-time setup (~5 min)">
        <p className="text-[#4A5D5A]">
          The OC-T80A is an <b>80mm thermal printer</b> with USB and Bluetooth.
          The browser can't talk to a Bluetooth printer directly, so we use a free
          Android app called <b>RawBT Print Service</b> as a "print bridge" between
          Chrome and the printer. Once paired, printing is a single tap from the
          POS receipt screen.
        </p>

        <Step n="1" title="Plug in & power on">
          <ul className="list-disc list-inside space-y-0.5">
            <li>Plug the printer into a wall outlet using the supplied power cable.</li>
            <li>Flip the rear power switch <b>ON</b>. The green status LED lights up.</li>
            <li>Open the top lid → drop in an <b>80mm × 50mm thermal roll</b> with the smooth/shiny side facing up → close the lid → tear off the slack paper.</li>
            <li>Press the <b>FEED</b> button briefly — paper should advance smoothly.</li>
          </ul>
        </Step>

        <Step n="2" title="Print a self-test">
          <ul className="list-disc list-inside space-y-0.5">
            <li>Power OFF the printer.</li>
            <li>Hold the <b>FEED</b> button while turning the printer ON.</li>
            <li>Release after ~2 seconds — the printer should print a self-test page showing model name, firmware version, and Bluetooth MAC address. <b>Save that page</b> — the Bluetooth MAC helps identify the device when pairing.</li>
          </ul>
        </Step>

        <Step n="3" title="Pair the printer with the tablet">
          <ul className="list-disc list-inside space-y-0.5">
            <li>On the Redmi Pad 2: <b>Settings → Connected devices → Bluetooth</b>.</li>
            <li>Tap <b>Pair new device</b>.</li>
            <li>Pick the device named <b>OC-T80A</b> (or similar, e.g. <b>BlueTooth Printer</b> / <b>BT-T80A</b>) — match against the MAC address on the self-test page if needed.</li>
            <li>If asked for a PIN, enter <b>0000</b> (or <b>1234</b>).</li>
            <li>Status should now read <b>Paired</b>.</li>
          </ul>
        </Step>

        <Step n="4" title="Install RawBT (free, 10 MB)">
          <ul className="list-disc list-inside space-y-0.5">
            <li>Open the <b>Play Store</b> on the tablet.</li>
            <li>Search for <b>RawBT Print Service</b> (developer: rawbt.ru).</li>
            <li>Tap <b>Install</b> — wait for it to finish.</li>
            <li>Open RawBT — accept the print-service and Bluetooth permissions.</li>
          </ul>
        </Step>

        <Step n="5" title="Connect RawBT to the printer">
          <ul className="list-disc list-inside space-y-0.5">
            <li>In RawBT: <b>Settings (⚙) → Connection</b>.</li>
            <li>Choose <b>Bluetooth</b> as the connection type.</li>
            <li>Tap <b>Bluetooth device</b> → pick <b>OC-T80A</b>.</li>
            <li>Go to <b>Printer model</b> → choose <b>Generic ESC/POS · 80mm</b> (or "Xprinter T80" if listed — both work for OC-T80A).</li>
            <li>Set <b>Paper width</b>: <b>80mm (576 dots)</b> and <b>Code page: PC437 (USA)</b>.</li>
            <li><b>Enable Auto-Cut</b>: in RawBT <b>Settings → Print → Cutter</b> turn on <b>"Cut after print"</b> (or "Full cut"). The OC-T80A's built-in cutter will then slice the paper at the exact end of every receipt — so length adjusts to the items and no paper is wasted.</li>
            <li>Tap <b>Print test page</b> — a small test receipt should come out and the cutter should fire at the bottom.</li>
          </ul>
          <div className="bg-[#1F3D24] border border-[#2F5233] rounded-lg p-3 mt-3 flex items-center gap-3">
            <div className="flex-1 text-xs text-[#D9DFDB]">
              <b className="text-white">Quick verify</b> — once RawBT is set up,
              tap the button below to send a test receipt from this app. If it
              prints, you're ready for Step 6.
            </div>
            <button
              type="button"
              onClick={() => { try { printTest(); } catch { /* user cancelled */ } }}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-[#F3C97A] hover:bg-[#E0B468] text-[#1C2826] text-xs font-bold shadow-sm"
              data-testid="print-test-receipt-btn"
            >
              <Printer className="h-3.5 w-3.5" />
              Print test receipt
            </button>
          </div>
        </Step>

        <Step n="6" title="Make RawBT the default print service">
          <ul className="list-disc list-inside space-y-0.5">
            <li>Tablet: <b>Settings → Connected devices → Connection preferences → Printing</b>.</li>
            <li>Tap <b>RawBT</b> and toggle it <b>ON</b>.</li>
            <li>Future Print buttons will offer RawBT automatically.</li>
          </ul>
        </Step>

        <Step n="7" title="(Optional) Enable Cash Drawer kick">
          <ul className="list-disc list-inside space-y-0.5">
            <li>Only needed if your printer is connected to a physical cash drawer via RJ-11 / RJ-12 cable.</li>
            <li>In RawBT: <b>Settings (⚙) → Cash drawer</b>.</li>
            <li>Enable <b>"Open cash drawer before printing"</b>.</li>
            <li>Now the POS <b>Open Drawer</b> button (and every receipt print) will pop the drawer open.</li>
          </ul>
        </Step>
      </Section>

      <Section icon={Usb} title="Alternative — USB cable (no Bluetooth)">
        <p>
          If you'd rather plug the printer directly into the tablet (skip Bluetooth entirely):
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>Use a <b>USB-OTG (USB-C to USB-A) adapter</b> on the Redmi Pad 2.</li>
          <li>Plug the OC-T80A's USB cable into the adapter → tablet asks "Allow printer access?" → tap <b>Allow</b>.</li>
          <li>In RawBT: <b>Settings → Connection → USB</b> → pick the printer.</li>
          <li>The rest of the flow (Step 6 onward) is identical to Bluetooth.</li>
          <li><b>Note:</b> with USB, the tablet must be plugged in <i>after</i> the printer is powered on.</li>
        </ul>
      </Section>

      <Section icon={Printer} title="2) Daily use — Print a receipt from the POS">
        <ol className="list-decimal list-inside space-y-1">
          <li>Sign in at <b>arbeminimart.com/admin</b> as a cashier or admin.</li>
          <li>Open the <b>POS</b> tab.</li>
          <li>Build the sale (tap items or scan barcodes), enter cash tendered, press <b>Complete Sale</b>.</li>
          <li>Pick a receipt method: <b>Email</b>, <b>Print</b>, or <b>No receipt</b>.</li>
          <li>If Print: Android shows the print dialog → pick <b>RawBT</b> → tap the round print button.</li>
          <li>Hand the receipt to the customer (auto-cut by the printer's built-in cutter).</li>
          <li>The dialog closes automatically → ready for the next sale.</li>
        </ol>
        <div className="mt-2 bg-[#E8F5E8] border border-[#2F5233] rounded-md p-2 text-xs text-[#1F3D24]">
          <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />
          <b>Tip:</b> Once RawBT is the default printer, prints go straight through without the picker dialog.
        </div>
      </Section>

      <Section icon={Camera} title="3) Camera barcode scanner — already works">
        <p>
          The Redmi Pad 2 rear camera scans barcodes directly inside Chrome — no extra device needed.
        </p>
        <div className="font-bold text-sm mt-2">First time</div>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Tap the <b>Scan</b> icon on the POS — Chrome will ask for camera permission. Tap <b>Allow</b>.</li>
          <li>Aim the rear camera at the product barcode.</li>
          <li>A green box appears on a successful scan and the item is added to the cart.</li>
        </ol>
        <div className="font-bold text-sm mt-2">Tips for fast scanning</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Hold the barcode <b>8–15 cm</b> from the camera under overhead light.</li>
          <li>The barcode should fill at least half the green guide box.</li>
          <li>For tiny products (AJI Vetsin sachets), get closer (~5 cm).</li>
          <li>For damaged barcodes, type the digits manually in the search box.</li>
        </ul>
      </Section>

      <Section icon={Wrench} title="4) Troubleshooting">
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b-2 border-[#E1E5DF]">
                <th className="py-2 pr-2 font-bold">Symptom</th>
                <th className="py-2 font-bold">Fix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E1E5DF]">
              {[
                ["Printer LED off / not responding", "Check the rear power switch is ON. Confirm power cable is firmly seated. Try a different outlet."],
                ["Receipt prints blank", "Paper roll loaded upside down — flip it (shiny side faces print head, smooth side up)."],
                ["Receipt prints garbled / wrong characters", "RawBT → Settings → Codepage → set to PC437 (USA) or CP1252. Also confirm paper width = 80mm (576 dots)."],
                ["Receipt margins are off (text too wide / cut off)", "RawBT → Settings → Paper width → 80mm. POS CSS already targets 80mm."],
                ["Print button does nothing", "RawBT not installed yet — go back to Step 4. OR RawBT not set as default printer — Step 6."],
                ["Auto-cutter doesn't cut", "RawBT → Settings → Cutter → enable. Some sellers ship the OC-T80A with the cutter disabled in firmware — power-cycle the printer if it still fails."],
                ["Bluetooth keeps disconnecting", "Move the tablet within 5m of the printer. Restart Bluetooth on both. Re-pair if needed."],
                ["Camera shows 'permission denied'", "Chrome → ⋮ → Settings → Site settings → Camera → enable for arbeminimart.com."],
                ["Barcode won't scan", "Clean the camera lens; try brighter light; or type digits manually."],
              ].map(([sym, fix]) => (
                <tr key={sym}>
                  <td className="py-2 pr-2 align-top text-[#4A5D5A]">{sym}</td>
                  <td className="py-2 align-top">{fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section icon={Battery} title="5) Roll & power maintenance">
        <ul className="list-disc list-inside space-y-1">
          <li><b>Paper:</b> Buy <b>80mm × 50m thermal rolls</b> with a 12.7mm (1/2") core (Lazada / Shopee — search "80mm thermal paper roll"). One roll ≈ 700 receipts.</li>
          <li><b>Power:</b> The OC-T80A runs on AC mains — no battery to charge. Keep it on a surge protector to protect against brownouts.</li>
          <li>Keep the printer <b>away from direct sunlight</b> — thermal paper darkens prematurely from heat.</li>
          <li>Clean the print head every 1-2 months with isopropyl alcohol on a cotton swab (printer powered OFF, head fully cooled).</li>
        </ul>
      </Section>

      <div className="bg-[#FFF8E1] border border-[#E67E22] rounded-xl p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-[#E67E22] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#1C2826]">
          <b>Need help?</b> Take a photo of the issue and message Arbe Loquero. Replacement RawBT setup takes 5 minutes.
        </div>
      </div>

      {/* Always-mounted hidden test-print block (visible to react-to-print's
          ref but hidden on screen via the .pos-print-receipt CSS). */}
      <div ref={testPrintRef} className="pos-print-receipt" data-testid="test-print-area">
          <div className="pr-center pr-heading">ARBE MINIMART</div>
          <div className="pr-center pr-small">Felix Taghap Street, Poblacion 2</div>
          <div className="pr-center pr-small">Oroquieta City 7207 · 0920 297 1717</div>
          <div className="pr-rule" />
          <div className="pr-center pr-small" style={{ fontWeight: 800 }}>** PRINTER TEST **</div>
          <div className="pr-center pr-tiny">Officom OC-T80A · 80mm</div>
          <div className="pr-center pr-tiny">{testStamp}</div>
          <div className="pr-rule" />
          <div className="pr-row"><span>Connection</span><span>OK</span></div>
          <div className="pr-row"><span>Paper width</span><span>80mm</span></div>
          <div className="pr-row"><span>Code page</span><span>PC437</span></div>
          <div className="pr-rule" />
          <div className="pr-center pr-small">If you can read this clearly,</div>
          <div className="pr-center pr-small">your setup is complete. ✓</div>
          <div className="pr-center pr-tiny" style={{ marginTop: "1mm" }}>arbeminimart.com</div>
        </div>
    </div>
  );
}
